import {
    ApprovalBudgetKind,
    BudgetGet,
    BudgetOpenItem,
    BudgetStatus,
    Decimal,
    SubscribeState,
    Ulid,
} from "@kase/my-marathon-communication";
import { ReadonlySignal, batch, computed, signal } from "@preact/signals";
import { upsert, upsertOne } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import { createListController } from "../utils";
import {
    Budget,
    BudgetCreateRequest,
    BudgetLine,
    BudgetLineOpenItem,
    BudgetListFilter,
    BudgetPhase,
    BudgetPhaseOpenItem,
    BudgetPrintRequest,
    BudgetSendExternalRequest,
    BudgetSendInternalRequest,
    BudgetUpdateRequest,
} from "./interfaces";
import { makeLine } from "./line";
import { makePhase } from "./phase";

const STUB_SIGNAL = computed((): Budget[] => []);

const ALL_STATUSES = [
    BudgetStatus.BudgetStatusExternal,
    BudgetStatus.BudgetStatusInternal,
    BudgetStatus.BudgetStatusNone,
];

export const makeBudget = ({ connection, company }: ApiSliceParams) => {
    const projectBudgets: Record<string, ReadonlySignal<Budget[]>> = {};
    const phase = makePhase({ connection, company });
    const line = makeLine({ connection, company });
    const openCache: Record<string, boolean> = {};

    const {
        loading,
        loaded,
        listen,
        items: allBudgets,
        whenSingleLoaded,
        getOrLoad,
    } = createListController<BudgetGet, Budget, BudgetListFilter>({
        connection,
        list: (con, filter) =>
            con.budget.items({
                company: company.value,
                project: filter.project
                    ? Ulid.fromString(filter.project)
                    : undefined,
                status: filter.statuses ?? ALL_STATUSES,
            }),
        getKey: (filter) =>
            `${filter.project}-${(filter.statuses ?? ALL_STATUSES).join(",")}`,
        load: (con, id) =>
            con.budget.get({
                company: company.value,
                id: Array.isArray(id) ? id : [id],
            }),
        view: (budget) => view(budget),
        name: "budget",
    });

    const removeItems = (
        budget: Budget,
        items: readonly Readonly<Omit<BudgetOpenItem, "data">>[],
    ) => {
        const phases = items
            .filter((item) => item.t === "P")
            .map((item) => item.id.toString());
        const oldPhases: BudgetPhase[] = [];
        const newPhases: BudgetPhase[] = [];

        budget.phases.peek().forEach((phase) => {
            if (phases.includes(phase.id)) {
                oldPhases.push(phase);
            } else {
                newPhases.push(phase);
            }
        });

        budget.hangingLines.value = [
            ...budget.hangingLines.peek(),
            ...oldPhases.flatMap((t) => t.lines.peek()),
        ];

        if (newPhases.length !== budget.phases.peek().length) {
            budget.phases.value = newPhases;
        }

        items
            .filter((line) => line.t !== "P")
            .forEach((line) => {
                const phase = budget.phases.value.find((phase) =>
                    phase.lines.value.some((t) => t._id.equals(line.id)),
                );

                if (phase) {
                    phase.lines.value = phase.lines
                        .peek()
                        .filter((t) => !t._id.equals(line.id));
                } else {
                    budget.hangingLines.value = budget.hangingLines
                        .peek()
                        .filter((t) => !t._id.equals(line.id));
                }
            });
    };

    const addItems = (
        budget: Budget,
        items: readonly Readonly<BudgetOpenItem>[],
    ) => {
        const phases = items
            .filter((item): item is BudgetPhaseOpenItem => item.t === "P")
            .map((item) => ({
                ...item,
                _id: item.id,
                id: item.id.toString(),
                lines: signal<BudgetLine[]>([]),
            }));

        if (phases.length) {
            const newPhases = [...budget.phases.peek()];
            let newHanging = budget.hangingLines.peek();

            phases.forEach((phase) => {
                const existing = newPhases.find((t) => t._id.equals(phase._id));
                const hanging = newHanging.filter((t) =>
                    t.data.phase?.equals(phase._id),
                );

                newHanging = newHanging.filter(
                    (t) => !t.data.phase?.equals(phase._id),
                );

                if (existing) {
                    Object.assign(existing.data, phase.data);

                    if (hanging.length) {
                        existing.lines.value = upsert(
                            existing.lines.peek().slice(),
                            hanging,
                            "id",
                        );
                    }
                } else {
                    newPhases.push(phase);

                    if (hanging.length) {
                        phase.lines.value = hanging;
                    }
                }
            });

            if (newHanging.length !== budget.hangingLines.peek().length) {
                budget.hangingLines.value = newHanging;
            }

            budget.phases.value = newPhases.sort(
                (a, b) => (a.data.prio ?? 0) - (b.data.prio ?? 0),
            );
        }

        items
            .filter((line): line is BudgetLineOpenItem => line.t !== "P")
            .forEach((line) => {
                const oldPhase = budget.phases.value.find((phase) =>
                    phase.lines.value.some((t) => t._id.equals(line.id)),
                );
                const linePhaseId = line.data.phase;
                const phase = linePhaseId
                    ? budget.phases.value.find((phase) =>
                          phase._id.equals(linePhaseId),
                      )
                    : null;

                const finalLine = {
                    ...line,
                    _id: line.id,
                    id: line.id.toString(),
                };

                if (phase) {
                    phase.lines.value = upsertOne(
                        phase.lines.peek().slice(),
                        finalLine,
                        "id",
                    ).sort(
                        (a, b) =>
                            (a.data.position ?? 0) - (b.data.position ?? 0),
                    );

                    if (oldPhase && oldPhase.id !== phase.id) {
                        oldPhase.lines.value = oldPhase.lines.value.filter(
                            (t) => t.id !== finalLine.id,
                        );
                    } else if (!oldPhase) {
                        budget.hangingLines.value = budget.hangingLines
                            .peek()
                            .filter((t) => t.id !== finalLine.id);
                    }
                } else {
                    budget.hangingLines.value = upsertOne(
                        budget.hangingLines.peek().slice(),
                        finalLine,
                        "id",
                    ).sort(
                        (a, b) =>
                            (a.data.position ?? 0) - (b.data.position ?? 0),
                    );

                    if (oldPhase) {
                        oldPhase.lines.value = oldPhase.lines.value.filter(
                            (t) => t.id !== finalLine.id,
                        );
                    }
                }
            });
    };

    const info = async (budget: Budget) => {
        const con = connection.value;

        budget.infoLoading.value = true;

        for await (const item of con.budget.info({
            company: company.value,
            id: [budget._id],
        })) {
            budget.info.value = item;
        }

        budget.infoLoading.value = false;
    };

    const open = async (budget: Budget) => {
        const con = connection.value;
        const sub = con.budget.open({
            company: company.value,
            id: budget._id,
        });

        openCache[budget.id] = true;
        budget.loading.value = true;

        for await (const event of sub) {
            if (event.kind === SubscribeState.Remove) {
                batch(() => {
                    removeItems(budget, event.values);
                });
            } else {
                batch(() => {
                    addItems(budget, event.values);
                });
            }

            if (event.kind === SubscribeState.Loaded) {
                budget.loading.value = false;
            }
        }
    };

    const view = (budget: BudgetGet) => {
        const finalBudget = {
            ...budget,
            id: budget.id.toString(),
            _id: budget.id,
            phases: signal([]),
            hangingLines: signal([]),
            loading: signal(false),
            info: signal(null),
            infoLoading: signal(false),
        };

        // @TODO: review other places where open happens on view
        // it triggers double open calls when item is updated instead of added
        //
        if (!openCache[finalBudget.id]) {
            open(finalBudget).catch(() => {});
            info(finalBudget).catch(() => {});
        }

        return finalBudget;
    };

    const create = (request: BudgetCreateRequest) => {
        const con = connection.value;
        const comp = company.value;

        return con.budget.create({
            company: comp,
            header: request.header,
            project: request.project,
            currency: request.currency,
            currency_rate: new Decimal(request.rate),
        });
    };

    const remove = async (id: Readonly<Ulid>) => {
        await connection.value.budget.delete({ company: company.value, id });
        // @TODO @TMP should be taken from subscription
        allBudgets.value = allBudgets
            .peek()
            .filter((budget) => !budget._id.equals(id));
    };

    const budgets = (id: string) => {
        if (!id) {
            return STUB_SIGNAL;
        }

        if (!(id in projectBudgets)) {
            projectBudgets[id] = computed(() =>
                allBudgets.value.filter(
                    (budget) => budget.project.toString() === id,
                ),
            );
        }

        return projectBudgets[id];
    };

    const print = (id: Readonly<Ulid>, params: BudgetPrintRequest = {}) => {
        return connection.value.budget.print({
            company: params.company ?? company.value,
            id,
            save: params.save ?? false,
            hours: params.hours ?? false,
            lines: params.lines ?? false,
            line_amount: params.lineAmount ?? false,
            phases: params.phases ?? false,
            phase_amount: params.phaseAmount ?? false,
            zero_amount_lines: params.zeroAmountLines ?? false,
            vat: params.vat ?? false,
            timeline: params.timeline ?? false,
            break_after: params.breakAfter ?? false,
            break_before: params.breakBefore ?? false,
        });
    };

    const update = ({ id, ...rest }: BudgetUpdateRequest) => {
        return connection.value.budget.update({
            company: company.value,
            id,
            ...rest,
        });
    };

    const getDocument = (id: Readonly<Ulid>) => {
        return connection.value.budget.getDocument({
            company: company.value,
            id,
        });
    };

    const sendInternal = async ({
        id,
        approver,
    }: BudgetSendInternalRequest) => {
        await connection.value.budget.update({
            company: company.value,
            id,
            status: BudgetStatus.BudgetStatusInternal,
        });
        await connection.value.approve.budget.approval.create({
            budget: id,
            approver,
            approval_kind: ApprovalBudgetKind.BudgetKindInternal,
            company: company.value,
        });
    };

    const sendExternal = async ({ id, email }: BudgetSendExternalRequest) => {
        await connection.value.budget.update({
            company: company.value,
            id,
            status: BudgetStatus.BudgetStatusExternal,
        });
        await connection.value.approve.budget.approval.create({
            budget: id,
            email,
            approval_kind: ApprovalBudgetKind.BudgetKindExternal,
            company: company.value,
        });
    };

    return {
        allBudgets,
        phase,
        line,
        budgets,
        loading,
        loaded,
        create,
        update,
        sendInternal,
        sendExternal,
        listen,
        remove,
        print,
        getDocument,
        // @TODO @TMP revisit
        whenSingleLoaded,
        getOrLoad,
    };
};
